import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { WeatherHuntingCard } from "../components/WeatherHuntingCard";
import { compressImage } from "../lib/imageCompress";
import { 
  Target, 
  Calendar, 
  Bell, 
  Clock,
  ChevronRight,
  Megaphone,
  Users,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BarChart3,
  CloudSun,
  Thermometer,
  Droplets,
  Wind,
  Image,
  MessageCircle,
  Send,
  Upload,
  Trash2,
  ChevronLeft,
  ChevronRightIcon,
  FileText,
  ExternalLink,
  Play
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// Extract YouTube video ID from various URL formats
function getYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

// Render text with clickable links and YouTube embeds
function RenderContent({ text, className = "" }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  const embeds = [];
  const rendered = parts.map((part, i) => {
    if (part.match(urlRegex)) {
      const ytId = getYouTubeId(part);
      if (ytId) {
        embeds.push(ytId);
        return (
          <a key={`link-${i}-${part.slice(0,20)}`} href={part} target="_blank" rel="noopener noreferrer"
             className="text-blue-600 hover:underline inline-flex items-center gap-1">
            <Play className="h-3 w-3" />YouTube
          </a>
        );
      }
      return (
        <a key={`link-${i}-${part.slice(0,20)}`} href={part} target="_blank" rel="noopener noreferrer"
           className="text-blue-600 hover:underline break-all inline-flex items-center gap-1">
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
          {part.length > 50 ? part.substring(0, 47) + '...' : part}
        </a>
      );
    }
    return <span key={`text-${i}`}>{part}</span>;
  });
  return (
    <div className={className}>
      <p className="whitespace-pre-wrap">{rendered}</p>
      {embeds.map((ytId) => (
        <div key={ytId} className="mt-2 rounded-lg overflow-hidden" style={{ maxWidth: 480 }}>
          <iframe
            width="100%" height="270"
            src={`https://www.youtube.com/embed/${ytId}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="rounded-lg"
          />
        </div>
      ))}
    </div>
  );
}

const DashboardPage = () => {
  const { user, token, hasRole } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [quotas, setQuotas] = useState([]);
  const [events, setEvents] = useState([]);
  const [animalCategories, setAnimalCategories] = useState([]);
  const [hoursStats, setHoursStats] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Galerija state
  const [galerija, setGalerija] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [slikaNaziv, setSlikaNaziv] = useState('');
  const [slikaOpis, setSlikaOpis] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Birthday popup
  const [showBirthdayPopup, setShowBirthdayPopup] = useState(false);

  // Managers can see detailed stats, all members can see anonymous stats
  const canViewDetailedStats = hasRole(["predsednik", "gospodar", "tajnik"]);

  useEffect(() => {
    fetchDashboardData();
    fetchWeather();
    fetchGalerija();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check birthday
  useEffect(() => {
    if (user?.rojstni_dan) {
      const today = new Date();
      const todayMD = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const rd = user.rojstni_dan;
      // Support YYYY-MM-DD or MM-DD
      const rdMD = rd.length > 5 ? rd.substring(5) : rd;
      if (todayMD === rdMD) {
        const dismissedKey = `birthday_dismissed_${today.getFullYear()}_${user.id}`;
        if (!sessionStorage.getItem(dismissedKey)) {
          setShowBirthdayPopup(true);
        }
      }
    }
  }, [user]);
  
  // Slideshow auto-advance
  useEffect(() => {
    if (galerija.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % galerija.length);
      }, 10000); // 10 seconds
      return () => clearInterval(interval);
    }
  }, [galerija.length]);
  
  const fetchGalerija = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/galerija`, { headers });
      setGalerija(response.data);
    } catch (error) {
      console.error("Error fetching galerija:", error);
    }
  };
  
  const handleUploadSlika = async () => {
    if (!selectedFile || !slikaNaziv) {
      toast.error("Izberite sliko in vnesite naziv");
      return;
    }
    setUploading(true);
    try {
      // Kompresija slike na klientu (nujno za mobitele – prepreči OOM/bel zaslon)
      let fileToSend = selectedFile;
      try {
        fileToSend = await compressImage(selectedFile, { maxDim: 2000, quality: 0.82 });
      } catch (compErr) {
        // Če kompresija spodleti (npr. HEIC brez podpore), uporabi original;
        // če je original > 10 MB, opozori uporabnika, da naj zmanjša.
        if (selectedFile.size > 10 * 1024 * 1024) {
          toast.error("Slika je prevelika za nalaganje. Prosim, zmanjšaj velikost slike ali izberi drugo.");
          setUploading(false);
          return;
        }
      }

      const formData = new FormData();
      formData.append('file', fileToSend);
      formData.append('naziv', slikaNaziv);
      if (slikaOpis) formData.append('opis', slikaOpis);

      await axios.post(`${API}/galerija/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // 60 s timeout – dovolj tudi za počasno mobilno omrežje po kompresiji
        timeout: 60000,
      });

      toast.success("Slika naložena");
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSlikaNaziv('');
      setSlikaOpis('');
      fetchGalerija();
    } catch (error) {
      // Globalen safety-net: ne pusti da React app crasha (bel zaslon).
      const d = error?.response?.data?.detail;
      let msg;
      if (Array.isArray(d)) {
        // Pydantic validation error format
        msg = d.map(e => e?.msg || JSON.stringify(e)).join("; ");
      } else if (typeof d === "string") {
        msg = d;
      } else if (d && typeof d === "object") {
        msg = d.msg || JSON.stringify(d);
      } else {
        msg = error?.message || "Napaka pri nalaganju slike";
      }
      toast.error(String(msg).slice(0, 300));
    } finally {
      setUploading(false);
    }
  };
  
  const handleDeleteSlika = async (slikaId) => {
    if (!window.confirm("Ali ste prepričani, da želite izbrisati to sliko?")) return;
    try {
      await axios.delete(`${API}/galerija/${slikaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Slika izbrisana");
      fetchGalerija();
    } catch (error) {
      toast.error("Napaka pri brisanju slike");
    }
  };
  
  const handleNovaObjava = () => { /* forum removed */ };
  const handleNovKomentar = () => { /* forum removed */ };
  const handleDeleteObjava = () => { /* forum removed */ };

  const fetchWeather = async () => {
    const doFetch = async (params = "") => {
      try {
        const response = await axios.get(`${API}/weather${params}`);
        setWeather(response.data);
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    };
    if ("geolocation" in navigator) {
      // Cache coordinates for 30 min to avoid re-prompts
      const cached = sessionStorage.getItem("user_coords");
      if (cached) {
        try {
          const { lat, lon, t } = JSON.parse(cached);
          if (Date.now() - t < 30 * 60 * 1000) {
            return doFetch(`?lat=${lat}&lon=${lon}`);
          }
        } catch { /* ignore */ }
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          sessionStorage.setItem("user_coords", JSON.stringify({ lat, lon, t: Date.now() }));
          doFetch(`?lat=${lat}&lon=${lon}`);
        },
        () => doFetch(""), // fallback to Rakek
        { timeout: 6000, maximumAge: 30 * 60 * 1000 }
      );
    } else {
      doFetch("");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Basic requests for all users
      const requests = [
        axios.get(`${API}/announcements`, { headers }),
        axios.get(`${API}/quotas`, { headers }),
        axios.get(`${API}/events`, { headers }),
        axios.get(`${API}/animal-categories`, { headers }),
        // Public stats for all users (no names)
        axios.get(`${API}/statistics/hours-public`, { headers }),
        axios.get(`${API}/statistics/attendance-public`, { headers })
      ];

      const responses = await Promise.all(requests);

      setAnnouncements(responses[0].data.slice(0, 5));
      setQuotas(responses[1].data);
      setEvents(responses[2].data.slice(0, 5));
      setAnimalCategories(responses[3].data);
      setHoursStats(responses[4].data);
      setAttendanceStats(responses[5].data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getQuotaPercentage = (quota) => {
    if (quota.plan === 0) return 0;
    return Math.min(Math.round((quota.odstreljeno / quota.plan) * 100), 100);
  };

  const getQuotaColor = (percentage) => {
    if (percentage <= 25) return "bg-green-500";
    if (percentage <= 50) return "bg-yellow-500";
    if (percentage <= 75) return "bg-orange-500";
    return "bg-red-500";
  };

  const getRoleLabel = (role) => {
    const labels = {
      predsednik: "Predsednik",
      gospodar: "Gospodar",
      blagajnik: "Blagajnik",
      tajnik: "Tajnik",
      upravnik_lovske_koce: "Upravnik lovske koče",
      referent_strelstvo: "Referent za strelstvo",
      clan: "Član"
    };
    return labels[role] || role;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("sl-SI", { day: "numeric", month: "short", year: "numeric" });
  };

  // Group quotas by category
  const groupedQuotas = quotas.reduce((acc, quota) => {
    const category = quota.kategorija_naziv || quota.kategorija;
    if (!acc[category]) {
      acc[category] = { items: [], totalPlan: 0, totalOdstreljeno: 0 };
    }
    acc[category].items.push(quota);
    acc[category].totalPlan += quota.plan;
    acc[category].totalOdstreljeno += quota.odstreljeno;
    return acc;
  }, {});

  const getWorkTypeLabel = (type) => {
    const labels = {
      delovna_akcija: "Delovna akcija",
      lov: "Lov",
      krmljenje: "Krmljenje",
      ciscenje_revira: "Čiščenje revira",
      vzdrzevanje_krmisca: "Vzdrževanje krmišča",
      sestanki: "Sestanki",
      straza: "Straža",
      drugo: "Drugo"
    };
    return labels[type] || type;
  };

  const dismissBirthday = () => {
    const today = new Date();
    const dismissedKey = `birthday_dismissed_${today.getFullYear()}_${user?.id}`;
    sessionStorage.setItem(dismissedKey, "true");
    setShowBirthdayPopup(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <OnboardingChecklist />
      {/* Birthday Popup */}
      <Dialog open={showBirthdayPopup} onOpenChange={(open) => { if (!open) dismissBirthday(); }}>
        <DialogContent className="sm:max-w-md text-center" data-testid="birthday-popup">
          <div className="py-6 space-y-4">
            <div className="text-6xl">🎂</div>
            <DialogTitle className="text-2xl font-bold text-primary">
              Vse najboljše, {user?.ime}!
            </DialogTitle>
            <p className="text-muted-foreground text-lg">
              Vse najboljše ti želi Lovska družina Rakek ter dober pogled in mirno roko.
            </p>
            <div className="text-4xl py-2">🎉🥂🎊</div>
            <Button onClick={dismissBirthday} className="mt-4" size="lg" data-testid="birthday-dismiss-btn">
              Hvala!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary">
          {(() => {
            const now = new Date();
            const h = now.getHours();
            const m = now.getMinutes();
            const cur = h * 60 + m;
            const parseHM = (s) => {
              if (!s || typeof s !== "string") return null;
              const [hh, mm] = s.split(":").map(Number);
              return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
            };
            const sunrise = parseHM(weather?.sonce_vzhod) ?? 6 * 60;
            const sunset = parseHM(weather?.sonce_zahod) ?? 20 * 60;
            const nineAM = 9 * 60;
            // Dober večer: pred sončnim vzhodom ali po sončnem zahodu
            if (cur < sunrise || cur >= sunset) return "Dober večer";
            // Dobro jutro: od sončnega vzhoda do 9. ure
            if (cur < nineAM) return "Dobro jutro";
            return "Dober dan";
          })()}, {user?.ime}!
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline" className="text-xs">
            {getRoleLabel(user?.vloga)}
          </Badge>
          <span className="hidden sm:inline">•</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date().toLocaleDateString("sl-SI", { weekday: "long", day: "numeric", month: "long" })}
          </span>
        </div>
      </div>

      {/* Weather + hunting index (below greeting) */}
      <WeatherHuntingCard />

      {/* Quick shortcuts – grid on mobile, buttons on desktop */}
      <div className="grid grid-cols-3 sm:hidden gap-2">
        <button
          onClick={() => navigate('/plan-odstrela')}
          className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl bg-green-50 border border-green-200 active:scale-95 transition"
          data-testid="mobile-quick-quotas"
        >
          <Target className="h-6 w-6 text-green-700" />
          <span className="text-xs font-medium text-green-800">Plan</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">{quotas.length}</Badge>
        </button>
        <button
          onClick={() => navigate('/koledar')}
          className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl bg-blue-50 border border-blue-200 active:scale-95 transition"
          data-testid="mobile-quick-events"
        >
          <Calendar className="h-6 w-6 text-blue-700" />
          <span className="text-xs font-medium text-blue-800">Dogodki</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">{events.length}</Badge>
        </button>
        <button
          onClick={() => navigate('/obvestila')}
          className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl bg-purple-50 border border-purple-200 active:scale-95 transition"
          data-testid="mobile-quick-announcements"
        >
          <Bell className="h-6 w-6 text-purple-700" />
          <span className="text-xs font-medium text-purple-800">Obvestila</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">{announcements.length}</Badge>
        </button>
      </div>
      <div className="hidden sm:flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9"
          onClick={() => navigate('/plan-odstrela')}
          data-testid="stat-quotas"
        >
          <Target className="h-4 w-4 text-green-600" />
          Plan odstrela
          <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{quotas.length}</Badge>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9"
          onClick={() => navigate('/koledar')}
          data-testid="stat-events"
        >
          <Calendar className="h-4 w-4 text-blue-600" />
          Dogodki
          <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{events.length}</Badge>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9"
          onClick={() => navigate('/obvestila')}
          data-testid="stat-announcements"
        >
          <Bell className="h-4 w-4 text-purple-600" />
          Obvestila
          <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{announcements.length}</Badge>
        </Button>
      </div>


      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
        {/* Quota Overview by Category */}
        <div 
          className="rounded-xl bg-green-50 border border-green-200 p-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/plan-odstrela')}
          data-testid="quota-overview-card"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-green-800 flex items-center gap-2">
              <Target className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              Plan odstrela
            </h3>
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
          </div>
          {Object.keys(groupedQuotas).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              {Object.entries(groupedQuotas).map(([category, data]) => {
                const pct = data.totalPlan > 0 ? Math.round((data.totalOdstreljeno / data.totalPlan) * 100) : 0;
                return (
                  <div key={category} className="bg-white/80 rounded-lg px-3 py-2.5 sm:py-3 text-center">
                    <div className="text-xs sm:text-sm font-medium text-green-700">{category}</div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-900 mt-0.5">{data.totalOdstreljeno}<span className="text-sm sm:text-base font-normal text-green-600">/{data.totalPlan}</span></div>
                    <div className="h-1.5 sm:h-2 rounded-full bg-green-100 mt-1.5 sm:mt-2">
                      <div className={`h-full rounded-full ${pct >= 80 ? 'bg-red-500' : pct >= 50 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recently closed quotas (last 7 days) */}
          {(() => {
            const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
            const closed = (quotas || []).filter(q => {
              if (!q.zaprto_datum) return false;
              if ((q.plan || 0) <= 0 || (q.odstreljeno || 0) < (q.plan || 0)) return false;
              const t = new Date(q.zaprto_datum).getTime();
              return Number.isFinite(t) && t >= sevenDaysAgo;
            });
            if (closed.length === 0) return null;
            return (
              <div className="mt-3 pt-3 border-t border-green-200/60 space-y-2" data-testid="closed-quotas-inside">
                {closed.map(q => {
                  let datumTxt = "";
                  try {
                    const d = new Date(q.zaprto_datum);
                    if (!isNaN(d.getTime())) {
                      const pad = (n) => String(n).padStart(2, "0");
                      datumTxt = `${pad(d.getDate())}. ${pad(d.getMonth() + 1)}. ${d.getFullYear()}`;
                    }
                  } catch (_) { /* ignore */ }
                  return (
                    <div
                      key={q.id}
                      className="w-full flex items-center justify-between gap-2 rounded-lg bg-red-600 text-white px-4 py-2.5 shadow-md animate-pulse"
                      data-testid={`closed-quota-chip-${q.podkategorija}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-white flex-shrink-0" />
                        <span className="font-bold tracking-wide uppercase text-sm sm:text-base truncate">
                          Odstrel {q.podkategorija_naziv} zaključen
                        </span>
                      </div>
                      {datumTxt && (
                        <span className="text-xs sm:text-sm font-semibold text-white/95 tabular-nums flex-shrink-0">
                          {datumTxt}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Announcements */}
        <Card data-testid="announcements-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-5 pt-4 sm:pt-5">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold flex items-center gap-2">
              <Megaphone className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              Obvestila
            </CardTitle>
            <Link to="/obvestila">
              <Button variant="ghost" size="sm" className="h-8 px-2">
                Več <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="px-4 sm:px-5 pb-4 sm:pb-5">
            <ScrollArea className="h-[320px] sm:h-[360px] lg:h-[400px]">
              {announcements.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Ni obvestil</p>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {announcements.map((ann) => (
                    <button
                      type="button"
                      key={ann.id}
                      onClick={() => setSelectedAnnouncement(ann)}
                      className={`w-full text-left p-3 sm:p-4 rounded-lg border transition-colors hover:bg-muted/50 ${ann.pomembno ? 'border-orange-200 bg-orange-50/50 hover:bg-orange-100/50 dark:border-orange-700/50 dark:bg-orange-900/20 dark:hover:bg-orange-900/30' : 'bg-card border-border'}`}
                      data-testid={`announcement-${ann.id}`}
                    >
                      <h4 className="font-semibold text-sm sm:text-base line-clamp-1">{ann.naslov}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                        {ann.vsebina}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatDate(ann.ustvarjeno)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Full announcement dialog */}
      <Dialog open={!!selectedAnnouncement} onOpenChange={(o) => { if (!o) setSelectedAnnouncement(null); }}>
        <DialogContent className="max-w-lg" data-testid="announcement-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Megaphone className="h-5 w-5 text-primary" />
              <span className="flex-1">{selectedAnnouncement?.naslov}</span>
              {selectedAnnouncement?.pomembno && (
                <Badge className="bg-orange-100 text-orange-800">Pomembno</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm whitespace-pre-wrap">{selectedAnnouncement?.vsebina}</p>
            {selectedAnnouncement?.povezava && (
              <a
                href={selectedAnnouncement.povezava}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="h-4 w-4" /> Odpri povezavo
              </a>
            )}
            <div className="pt-3 border-t text-xs text-muted-foreground flex items-center justify-between">
              <span>Avtor: {selectedAnnouncement?.avtor_ime}</span>
              <span>{selectedAnnouncement?.ustvarjeno && formatDate(selectedAnnouncement.ustvarjeno)}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upcoming Events */}
      <Card data-testid="events-overview-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-5 pt-4 sm:pt-5">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Prihajajoči dogodki
          </CardTitle>
          <Link to="/dogodki">
            <Button variant="ghost" size="sm" className="h-8 px-2">
              Več <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="px-4 sm:px-5 pb-4 sm:pb-5">
          {events.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Ni prihajajočih dogodkov</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {[...events].sort((a, b) => new Date(a.datum) - new Date(b.datum)).map((event) => (
                <div 
                  key={event.id} 
                  className="p-3 sm:p-4 rounded-lg border bg-card hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                  onClick={() => navigate('/dogodki')}
                  data-testid={`event-${event.id}`}
                >
                  <Badge variant="secondary" className="text-xs mb-2">
                    {event.tip}
                  </Badge>
                  <h4 className="font-semibold text-sm sm:text-base line-clamp-2">{event.naslov}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(event.datum)}
                    {event.cas && ` ob ${event.cas}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Simple Hours Statistics - at the bottom */}
      {hoursStats && (
        <Card data-testid="hours-stats-card">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Delovne ure
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Overall Progress */}
              <div className="p-4 rounded-lg border bg-card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Skupna realizacija</span>
                  <Badge variant={hoursStats.overall_percentage >= 80 ? "default" : "secondary"}>
                    {hoursStats.overall_percentage}%
                  </Badge>
                </div>
                <Progress value={hoursStats.overall_percentage} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {hoursStats.total_completed_hours} / {hoursStats.total_mandatory_hours} ur
                </p>
              </div>

              {/* Not Started */}
              <div className="p-4 rounded-lg border bg-red-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium">Brez ur</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{hoursStats.not_started}</p>
                <p className="text-xs text-muted-foreground">
                  članov brez opravljenih ur
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Galerija Slideshow */}
      <Card data-testid="galerija-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 pt-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Image className="h-5 w-5 text-primary" />
            Galerija
          </CardTitle>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="upload-galerija-btn">
                <Upload className="h-4 w-4 mr-1" />
                Naloži sliko
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Naloži sliko v galerijo</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium">Slika *</label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e?.target?.files?.[0];
                      if (!f) return;
                      // Trdna omejitev, da preprečimo OOM na mobitelu; kompresija bo tako ali tako zmanjšala velikost.
                      const MAX = 25 * 1024 * 1024;
                      if (f.size > MAX) {
                        toast.error("Datoteka je prevelika (>25 MB). Prosim izberi manjšo sliko.");
                        e.target.value = "";
                        return;
                      }
                      setSelectedFile(f);
                    }}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Slika se pred pošiljanjem samodejno zmanjša (do 25 MB izvirno)</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Naziv *</label>
                  <Input
                    value={slikaNaziv}
                    onChange={(e) => setSlikaNaziv(e.target.value)}
                    placeholder="Naziv slike"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Opis</label>
                  <Textarea
                    value={slikaOpis}
                    onChange={(e) => setSlikaOpis(e.target.value)}
                    placeholder="Kratek opis slike..."
                    rows={2}
                  />
                </div>
                <Button onClick={handleUploadSlika} disabled={uploading} className="w-full">
                  {uploading ? 'Nalagam...' : 'Naloži'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {galerija.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Ni slik v galeriji</p>
              <p className="text-sm">Kliknite "Naloži sliko" za dodajanje.</p>
            </div>
          ) : (
            <div className="relative">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
                <img
                  src={`${API}/galerija/slika/${galerija[currentSlide]?.pot_do_slike}`}
                  alt={galerija[currentSlide]?.naziv}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h4 className="text-white font-medium">{galerija[currentSlide]?.naziv}</h4>
                  {galerija[currentSlide]?.opis && (
                    <p className="text-white/80 text-sm">{galerija[currentSlide]?.opis}</p>
                  )}
                  <p className="text-white/60 text-xs mt-1">
                    {galerija[currentSlide]?.avtor_ime}
                  </p>
                </div>
                {galerija.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide((prev) => (prev - 1 + galerija.length) % galerija.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setCurrentSlide((prev) => (prev + 1) % galerija.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </>
                )}
                {hasRole(['predsednik', 'tajnik', 'gospodar']) && (
                  <button
                    onClick={() => handleDeleteSlika(galerija[currentSlide]?.id)}
                    className="absolute top-2 right-2 bg-red-500/80 text-white p-2 rounded-full hover:bg-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {galerija.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-3">
                  {galerija.map((img, idx) => (
                    <button
                      key={img.id || img.name || `slide-${idx}`}
                      onClick={() => setCurrentSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentSlide ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Forum removed – per user request */}
    </div>
  );
};

export default DashboardPage;
